import React from 'react';
import _ from 'lodash';

import { SidebarFiltersSection, SidebarFiltersLinkList, SidebarFiltersLinkListItem } from 'components/sidebar-filters';
import { CheckboxGroup, SearchBarFilter } from 'components/core';
import useTranslation from 'hooks/use-translation';
import { ViewMoreLinkToggle } from 'components/view-more-link-toggle';
import styled from 'styled-components';
import { useBrandsFilter } from './use-brands-filter';
import { Brands } from './brands-filter.types';

type BrandsFilterProps = {
  brands: Brands;
};

export default function BrandsFilter({ brands }: BrandsFilterProps): JSX.Element {
  const { t } = useTranslation();
  const {
    brandsFromQueryParams,
    brandOptionsToRender,
    defaults,
    handleBrandSelection,
    href,
    query,
    route,
    setSearch,
    search,
    setExpanded,
    expanded,
    onBrandsPage,
    displaySearchBar,
    displayViewMore,
  } = useBrandsFilter(brands);
  // on brands page from the UI store has a delay when changing from certain pages.
  // This caused the wrong component to display for a second or 2 on slow networks.
  const brandOptions = brandOptionsToRender();
  const brandListItemOptions = expanded ? brandOptions : brandOptions.slice(0, 6);

  let content: React.ReactNode;

  if (onBrandsPage) {
    content = (
      <SidebarFiltersLinkList>
        {brandListItemOptions.length > 0 ? (
          brandListItemOptions.map(({ key, label, value }) => (
            <SidebarFiltersLinkListItem
              href={`${href}/${value}`}
              key={key}
              label={label}
              queryParam='brands'
              route={`${route}/[brandId]`}
              selected={value === query.brandId}
              title={label}
              value={defaults.brands}
            />
          ))
        ) : (
          <NoResults>No results</NoResults>
        )}
        {displayViewMore && (
          <li>
            <ViewMoreLinkToggle expanded={expanded} onToggle={() => setExpanded(!expanded)} />
          </li>
        )}
      </SidebarFiltersLinkList>
    );
  } else if (brandOptions.length > 0) {
    content = (
      <CheckboxGroup
        options={brandOptions}
        selectedOptions={brandsFromQueryParams}
        onSelect={(brand: string) => handleBrandSelection(_.kebabCase(brand))}
      />
    );
  } else {
    content = <NoResults onbrandspage={onBrandsPage}>No results</NoResults>;
  }

  return (
    <SidebarFiltersSection startsOpen heading={t('common.brand_plural', 'Brands')}>
      {displaySearchBar && (
        <SearchBarFilter
          placeholder={t('search-brands-placeholder', 'Search Brands...')}
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(_.toLower(e.target.value))}
        />
      )}
      <>{content}</>
    </SidebarFiltersSection>
  );
}

const NoResults = styled.div<{ onbrandspage?: boolean }>`
  font-size: 13px;
  font-weight: 400;
  line-height: 1.125rem;
  margin-top: ${({ onbrandspage }) => (onbrandspage ? '0' : '0.75rem')};
`;
